import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL, SHOW_CARD_PRIORITY, SHOW_RECURSIVE_TASK } from '../../config';
import { Session } from '../utils/session';
import { toDisplayDate, toInputDateTime, toApiDateTimeLocal, isDueDateOverdue } from '../../utils/dateOnly';
import {
  RECURSIVE_FREQUENCIES,
  getRecursiveValueOptions,
  normalizeRecursiveFrequency,
} from '../../utils/recursiveTask';

export default function TaskCardModal({
  card,
  statusName,
  users,
  priorities,
  onClose,
  onSaved,
  onDeleted,
}) {
  const token = Session.getToken();
  const currentUserId = Session.getUserId();

  const [claimant, setClaimant] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [priorityText, setPriorityText] = useState('');
  const [cpid, setCpid] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isCallMeeting, setIsCallMeeting] = useState(false);
  const [recursiveTask, setRecursiveTask] = useState(false);
  const [recursiveFrequency, setRecursiveFrequency] = useState('');
  const [recursiveValue, setRecursiveValue] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!card) return;
    const titleParts = (card.cardTitle || '').split(' - ');
    setClaimant(titleParts[0] || card.cardTitle || '');
    setAssignedUserId(card.assignedUserID ? String(card.assignedUserID) : '');
    setPriorityText(card.priorityName || '');
    setCpid(card.cpid ? String(card.cpid) : '');
    setDueDate(toInputDateTime(card.dueDate));
    setIsCallMeeting(Boolean(card.isCallMeeting ?? card.IsCallMeeting));
    setRecursiveTask(Boolean(card.recursiveTask ?? card.RecursiveTask));
    setRecursiveFrequency(normalizeRecursiveFrequency(card.recursiveFrequency ?? card.RecursiveFrequency));
    const rv = card.recursiveValue ?? card.RecursiveValue;
    setRecursiveValue(rv != null && rv !== '' ? String(rv) : '');
    setError(null);
  }, [card]);

  const recursiveReferenceDate = useMemo(() => {
    if (dueDate) {
      const parsed = new Date(dueDate);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  }, [dueDate]);

  const recursiveValueOptions = useMemo(
    () => getRecursiveValueOptions(recursiveFrequency, recursiveReferenceDate),
    [recursiveFrequency, recursiveReferenceDate]
  );

  useEffect(() => {
    if (!recursiveTask || !recursiveFrequency || !recursiveValue) return;
    const valid = recursiveValueOptions.some(
      (opt) => String(opt.value) === String(recursiveValue)
    );
    if (!valid) setRecursiveValue('');
  }, [recursiveTask, recursiveFrequency, recursiveValue, recursiveValueOptions]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''} ${u.userName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [users, userSearch]);

  const selectedUserLabel = useMemo(() => {
    if (!assignedUserId) return 'Select..';
    const u = users.find((x) => String(x.userId) === String(assignedUserId));
    if (!u) return 'Select..';
    return u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.userName;
  }, [assignedUserId, users]);

  const handleSave = async () => {
    if (!card) return;
    setSaving(true);
    setError(null);
    try {
      const matchedPriority = priorities?.find(
        (p) => p.priorityName?.toLowerCase() === priorityText.trim().toLowerCase()
      );
      const resolvedCpid = matchedPriority?.cpid ?? (cpid ? Number(cpid) : null);

      const res = await fetch(`${API_BASE_URL}/api/Cards/card-update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardID: card.cardID,
          cardTitle: claimant.trim(),
          description: card.description || '',
          assignedUserID: assignedUserId ? Number(assignedUserId) : null,
          cpid: resolvedCpid,
          dueDate: toApiDateTimeLocal(dueDate),
          createdUserID: Number(currentUserId) || 0,
          isCallMeeting,
          recursiveTask,
          recursiveFrequency: recursiveTask && recursiveFrequency ? recursiveFrequency : null,
          recursiveValue: recursiveTask && recursiveValue ? Number(recursiveValue) : null,
        }),
      });
      if (!res.ok) {
        let message = `Failed to save changes (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!card || !window.confirm('Delete this card?')) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Cards/delete/${card.cardID}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let message = `Failed to delete card (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      if (onDeleted) await onDeleted();
      onClose();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!card) return null;

  return createPortal(
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="task-modal-close-x" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <div className="task-modal-header">
          <div className="task-modal-header-icon">
            <i className="fas fa-list-ul" />
          </div>
          <div className="task-modal-header-text">
            <h2 className="task-modal-title">{card.cardTitle}</h2>
            <p className="task-modal-subtitle">in list: {statusName || card.statusName || 'OPEN'}</p>
          </div>
        </div>

        <div className="task-modal-body">
          <div className="task-modal-grid">
            <div className="task-modal-field">
              <label>DESCRIPTION</label>
              <textarea
                rows={3}
                value={claimant}
                onChange={(e) => setClaimant(e.target.value)}
              />
            </div>
            <div className="task-modal-field">
              <label>ASSIGNED TO</label>
              <div className="task-modal-user-select">
                <button
                  type="button"
                  className="task-modal-user-trigger"
                  onClick={() => setShowUserList((s) => !s)}
                >
                  <i className="fas fa-user" />
                  <span>{selectedUserLabel}</span>
                  <i className="fas fa-chevron-down task-modal-chevron" />
                </button>
                {showUserList && (
                  <div className="task-modal-user-dropdown">
                    <div className="task-modal-user-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        placeholder="Select.."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    <div className="task-modal-user-list">
                      <label className="task-modal-user-option">
                        <input
                          type="radio"
                          name="assignedUser"
                          checked={!assignedUserId}
                          onChange={() => setAssignedUserId('')}
                        />
                        <span>Unassigned</span>
                      </label>
                      {filteredUsers.map((u) => (
                        <label key={u.userId} className="task-modal-user-option">
                          <input
                            type="radio"
                            name="assignedUser"
                            checked={String(assignedUserId) === String(u.userId)}
                            onChange={() => {
                              setAssignedUserId(String(u.userId));
                              setShowUserList(false);
                            }}
                          />
                          <span>
                            {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.userName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {card.assignedByName && (
                <p className="task-modal-assigned-by text-muted small mb-0 mt-2">
                  Assigned by: <strong>{card.assignedByName}</strong>
                </p>
              )}
            </div>
            {SHOW_CARD_PRIORITY && (
              <div className="task-modal-field">
                <label>PRIORITY</label>
                {priorities && priorities.length > 0 ? (
                  <select
                    className="task-modal-priority-select"
                    value={priorityText}
                    onChange={(e) => setPriorityText(e.target.value)}
                  >
                    <option value="">No Priority</option>
                    {priorities.map((p) => (
                      <option key={p.cpid} value={p.priorityName || ''}>
                        {p.priorityName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter priority"
                    value={priorityText}
                    onChange={(e) => setPriorityText(e.target.value)}
                  />
                )}
              </div>
            )}
            <div className="task-modal-field">
              <label>CREATED AT</label>
              <div className="task-modal-readonly-value">
                {card.createdAt ? toDisplayDate(card.createdAt) : '—'}
              </div>
            </div>
            <div className="task-modal-field">
              <label>DUE DATE &amp; TIME</label>
              <input
                type="datetime-local"
                className={`task-modal-datetime-input${dueDate && isDueDateOverdue(dueDate) ? ' task-modal-date-overdue' : ''}`}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="task-modal-field task-modal-field-checkbox">
              <label className="task-modal-checkbox-wrap" htmlFor="task-call-meeting">
                <span className="task-modal-checkbox-heading">CALL/MEETING</span>
                <input
                  id="task-call-meeting"
                  type="checkbox"
                  className="task-modal-checkbox-input"
                  checked={isCallMeeting}
                  onChange={(e) => setIsCallMeeting(e.target.checked)}
                />
              </label>
            </div>
            {SHOW_RECURSIVE_TASK && (
              <>
                <div className="task-modal-field task-modal-field-checkbox">
                  <label className="task-modal-checkbox-wrap" htmlFor="task-recursive">
                    <span className="task-modal-checkbox-heading">RECURSIVE TASK</span>
                    <input
                      id="task-recursive"
                      type="checkbox"
                      className="task-modal-checkbox-input"
                      checked={recursiveTask}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRecursiveTask(checked);
                        if (!checked) {
                          setRecursiveFrequency('');
                          setRecursiveValue('');
                        }
                      }}
                    />
                  </label>
                </div>
                {recursiveTask && (
                  <>
                    <div className="task-modal-field">
                      <label>FREQUENCY</label>
                      <select
                        className="task-modal-priority-select"
                        value={recursiveFrequency}
                        onChange={(e) => {
                          setRecursiveFrequency(e.target.value);
                          setRecursiveValue('');
                        }}
                      >
                        <option value="">Select frequency</option>
                        {RECURSIVE_FREQUENCIES.map((freq) => (
                          <option key={freq} value={freq}>{freq}</option>
                        ))}
                      </select>
                    </div>
                    {recursiveFrequency && (
                      <div className="task-modal-field">
                        <label>{recursiveFrequency === 'Monthly' ? 'DAY OF MONTH' : 'DAY'}</label>
                        <select
                          className="task-modal-priority-select"
                          value={recursiveValue}
                          onChange={(e) => setRecursiveValue(e.target.value)}
                        >
                          <option value="">Select...</option>
                          {recursiveValueOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </div>

        <div className="task-modal-footer">
          <button
            type="button"
            className="task-modal-btn task-modal-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            <i className="fas fa-save" /> Save Changes
          </button>
          <button
            type="button"
            className="task-modal-btn task-modal-btn-delete"
            onClick={handleDelete}
            disabled={deleting}
          >
            <i className="fas fa-trash-alt" /> Delete
          </button>
          <button type="button" className="task-modal-btn task-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
